/**
 * Chaves de cache do TanStack Query — fábrica centralizada evita
 * mismatch silencioso entre invalidate e fetch.
 *
 * Toda listagem leva o `clientId`: ao trocar de empresa o cache da anterior não é
 * exibido nem reaproveitado. `null` é a listagem sem empresa (usuário sem vínculo).
 *
 * Duas chaves fogem disso porque são anteriores ao vínculo: o escritório do endereço
 * (por host) e o convite (por token).
 */
import type { FetchDocumentsParams, GuideStatusFilter } from '~/api/documents';
import type { FetchInvoicesParams } from '~/api/invoices';

export type ClientScope = number | null;

export interface InvoiceListFilters {
  status?: FetchInvoicesParams['status'];
  mesAno?: string;
}

export const queryKeys = {
  invoices: {
    all: ['invoices'] as const,
    list: (clientId: ClientScope, params: FetchInvoicesParams = {}) =>
      ['invoices', 'list', clientId, params] as const,
    infinite: (clientId: ClientScope, filters: InvoiceListFilters = {}) =>
      ['invoices', 'infinite', clientId, filters] as const,
    summary: (clientId: ClientScope) => ['invoices', 'summary', clientId] as const,
    detail: (id: string) => ['invoices', 'detail', id] as const,
  },
  /**
   * Documentos da empresa (E14). O push de um post e o "Paguei" invalidam `all`. O feed e as
   * guias de cada situação (E16) levam `'infinite'`, que o cache persistido não grava: um cursor
   * guardado pode não existir mais. O arquivo leva `'file'`, que também não é gravado: o blob não
   * vira JSON, e o arquivo de uma empresa não fica no aparelho depois que o visualizador fecha.
   */
  documents: {
    all: ['documents'] as const,
    feed: (clientId: ClientScope) => ['documents', 'feed', 'infinite', clientId] as const,
    sections: (clientId: ClientScope) => ['documents', 'sections', clientId] as const,
    list: (clientId: ClientScope, params: FetchDocumentsParams = {}) =>
      ['documents', 'list', clientId, params] as const,
    file: (clientId: ClientScope, id: string) => ['documents', 'file', clientId, id] as const,
    guides: (clientId: ClientScope, status: GuideStatusFilter) =>
      ['documents', 'guides', 'infinite', clientId, status] as const,
  },
  codigosServico: (clientId: ClientScope) => ['codigos-servico', clientId] as const,
  /** Quem tem acesso à empresa; a tela "Usuários da empresa" invalida as duas listas juntas. */
  companyUsers: {
    list: (clientId: ClientScope) => ['company-users', clientId] as const,
  },
  companyInvitations: {
    list: (clientId: ClientScope) => ['company-invitations', clientId] as const,
  },
  auth: {
    me: ['auth', 'me'] as const,
  },
  /** Por host, não por empresa: o escritório é do endereço em que o app abriu. */
  tenant: {
    byHost: (host: string) => ['tenant', host] as const,
  },
  /** Por token, não por empresa: o convite vem antes do vínculo, e quem o escopa é o token. */
  invitations: {
    byToken: (token: string) => ['invitations', token] as const,
  },
} as const;
